import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { SettingsState } from '@/core/persistence/schema/v1';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { liftLampsOnTop, type HandlerEnv } from './placement/handlers';
import { refreshCoziness } from './coziness';
import { grantDailyMail } from './mailbox';
import { orderTick } from './orders';
import { resetShopOnBoot, shopTick } from './shop';
import { studioTick } from './studio';
import { dayKeyOf } from './time';

/** Longest single domain step; throttled background timers must not make the shop "jump" (§14.0). */
const MAX_STEP_MS = 1000;

export function advanceTime(draft: GameSave, cmd: CommandOf<'advanceTime'>, env: HandlerEnv): CommandResult {
  const dt = Math.min(MAX_STEP_MS, Math.max(0, cmd.dtMs));
  draft.clock.playTimeMs += dt;
  const today = dayKeyOf(cmd.nowWallMs);
  const events: DomainEvent[] = [];
  // Clock rollback safe: lastSeenDayKey only ever moves forward.
  if (today > draft.clock.lastSeenDayKey) {
    events.push({ type: 'DAY_CHANGED', from: draft.clock.lastSeenDayKey, to: today });
    draft.clock.lastSeenDayKey = today;
    // Playing past midnight: today's mail arrives without a restart.
    events.push(...grantDailyMail(draft, env.content, cmd.nowWallMs));
  }
  events.push(...shopTick(draft, env), ...orderTick(draft, env), ...studioTick(draft, env));
  return { ok: true, events };
}

export function bootSession(draft: GameSave, cmd: CommandOf<'bootSession'>, env: HandlerEnv): CommandResult {
  const today = dayKeyOf(cmd.nowWallMs);
  if (today > draft.clock.lastSeenDayKey) draft.clock.lastSeenDayKey = today;
  draft.contentVersion = env.content.version;
  resetShopOnBoot(draft);
  liftLampsOnTop(draft, env.content);
  // Always emit something so every system (unlocks, suggestions, achievements) reconciles on load.
  return {
    ok: true,
    events: [
      { type: 'SESSION_BOOTED' },
      ...grantDailyMail(draft, env.content, cmd.nowWallMs),
      ...refreshCoziness(draft, ['home', 'minimart', 'studio'], env),
    ],
  };
}

export function navigateScene(draft: GameSave, cmd: CommandOf<'navigateScene'>): CommandResult {
  if (!draft.scenes.includes(cmd.sceneId)) return { ok: false, error: 'LOCKED' };
  draft.lastSession.sceneId = cmd.sceneId;
  return { ok: true, events: [{ type: 'SCENE_ENTERED', sceneId: cmd.sceneId }] };
}

export function updateLastSession(
  draft: GameSave,
  cmd: CommandOf<'updateLastSession'>,
  env: HandlerEnv,
): CommandResult {
  if (cmd.patch.cameras) draft.lastSession.cameras = { ...draft.lastSession.cameras, ...cmd.patch.cameras };
  if (cmd.patch.panel) {
    draft.lastSession.panel = cmd.patch.panel;
    if (cmd.patch.panelParams && Object.keys(cmd.patch.panelParams).length > 0)
      draft.lastSession.panelParams = cmd.patch.panelParams;
    else delete draft.lastSession.panelParams;
  }
  draft.lastSession.savedAtWallMs = env.nowWallMs;
  return { ok: true, events: [] };
}

export function toggleFavorite(draft: GameSave, cmd: CommandOf<'toggleFavorite'>): CommandResult {
  const favs = draft.inventory.favorites;
  draft.inventory.favorites = favs.includes(cmd.itemId)
    ? favs.filter((id) => id !== cmd.itemId)
    : [...favs, cmd.itemId];
  return { ok: true, events: [] };
}

export function updateSettings(draft: GameSave, cmd: CommandOf<'updateSettings'>): CommandResult {
  const keys = Object.keys(cmd.patch) as (keyof SettingsState)[];
  draft.settings = { ...draft.settings, ...cmd.patch };
  return { ok: true, events: [{ type: 'SETTINGS_CHANGED', keys }] };
}
